import * as Core from '@gongxigames/framework/core';
import {
    ComponentModuleConfig,
    ComponentModuleViewConfig,
    ComponentsConfigs,
    CORE_SETTINGS_PARAMETERS_SCHEMA,
    disabledComponentByDefault,
    Settings,
} from '@gongxigames/framework/core';
import {Logic} from '@gongxigames/framework/slot/services/logic';
import {TemplateUtils} from '@gongxigames/framework/slot/services/TemplateUtils';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {SoundPlayer} from '@gongxigames/framework/slot/services/SoundPlayer';
import {WinPresentationUtils} from '@gongxigames/framework/slot/services/WinPresentationUtils';
import {ReelsScaleService} from '@gongxigames/framework/slot/services/ReelsScaleService';
import {PixiJSDevtoolsService} from '@gongxigames/framework/slot/services/PixiJSDevtoolsService';
import {AutotestService} from '@gongxigames/framework/slot/services/AutotestService';
import {LoggerService} from '@gongxigames/framework/slot/services/LoggerService';
import {SplashScreenService} from '@gongxigames/framework/slot/services/SplashScreenService';
import {ReelsConstantsService} from '@gongxigames/framework/slot/services/ReelsConstantsService';
import {InitializationEnvironmentSettingsService} from '@gongxigames/framework/slot/services/InitializationEnvironmentSettingsService';
import {SLOT_SETTINGS_PARAMETERS_SCHEMA} from '@gongxigames/framework/slot/configs/SlotSettings';
import {PrizeSymbolsUtils} from '@gongxigames/framework/slot/services/PrizeSymbolsUtils';
import * as Modules from '@gongxigames/framework/slot/modules';
import {ScaleService} from '@gongxigames/framework/slot/services/ScaleService';
import {GameInfoService} from '@gongxigames/framework/slot/index';
import {SLOT_V2_SERVICES} from '@gongxigames/framework/slotV2/services/index';
import {EventTrackerService} from '@gongxigames/framework/slot/services/EventTrackerService';

/* develblock:start */
import {StatsService} from '@gongxigames/framework/slot/index';
/* develblock:end */

Settings.registerSettingsParametersSchema(CORE_SETTINGS_PARAMETERS_SCHEMA);
Settings.registerSettingsParametersSchema(SLOT_SETTINGS_PARAMETERS_SCHEMA);

export const slotComponentsConfig: ComponentsConfigs = {
    services: [
        // core services
        Core.Browser,
        Core.InputGestures,
        Core.Device,
        Core.Settings,
        InitializationEnvironmentSettingsService,
        Core.EventManagerWithAsync,
        Core.SlotEventAdapter,
        Core.CommandLineInterfaceService,
        Core.LanguageService,
        Core.UpdateManager,
        Core.System,
        Core.ResolutionManager,
        Core.ScreenManagerV2,
        Core.ScreenManager,
        Core.VisibilityManager,
        Core.ResourceLoader,
        Core.KeyboardModule,
        Core.ObjectBuilder,
        Core.DisplayObjectUtils,
        Core.FullscreenManager,
        Core.CurrencyFormatter,
        Core.SpineUtils,
        // slot services
        TemplateUtils,
        Logic,
        ResourceManager,
        SoundManager,
        SoundPlayer,
        Core.StoreSetter,
        ReelsScaleService,
        ScaleService,
        WinPresentationUtils,
        /* develblock:start */
        PixiJSDevtoolsService,
        StatsService,
        /* develblock:end */
        AutotestService,
        LoggerService,
        EventTrackerService,
        SplashScreenService,
        ReelsConstantsService,
        PrizeSymbolsUtils,
        GameInfoService,

        ...SLOT_V2_SERVICES,
    ],
    modules: [
        // initial
        Modules.SystemPopupsManager,
        Modules.MessageModule,
        Modules.LoadingScreenModule,
        Modules.IOSFullscreenModule,
        Modules.WaitingModule,
        Modules.FlowModule,
        Modules.NetworkModule,
        Modules.InactivityTimerModule,
        Modules.RealityCheckModule,
        // free rounds
        Modules.GameSessionPromotionModule,
        Modules.SingleSetPromotionModule,
        Modules.MultiSetsPromotionModule,
        Modules.MultiSetsMulticampaignPromotionModule,
        Modules.PromotionCounterModule,
        Modules.BigWinModule,
        Modules.KeypadModule,
        Modules.SpinButtonModule,
        disabledComponentByDefault(Modules.AnticipatorModule),
        Modules.ReelsModule,
        Modules.AutoplayModule,
        Modules.AutoplayOptionsModule,
        Modules.FreeSpinsModule,
        Modules.GenericPopupModule,
        Modules.KeypadInfoElementsModule,
        Modules.KeypadControlsModule,
        Modules.BackgroundModule,
        Modules.WinPresentationModule,
        Modules.WinMeterModule,
        /* develblock:start */
        Modules.CheatsModule,
        Modules.RewindModule,
        /* develblock:end */
        Modules.InfoPage,
        Modules.GameRules,
        Modules.GameHistory,
        disabledComponentByDefault(Modules.SelectBonusModule),
        Modules.WaterMarkModuleFullscreen,
        Modules.BottomPanelModule,
        Modules.MenuBarModule,
        Modules.GameFrameFullScreenModule,
        Modules.BuyBonusButtonModule,
        Modules.BuyPopUpModule,
        Modules.BuyBonusBalanceModule,
        Modules.CoinFlightModule,
        Modules.BuyFeatureModule,
        Modules.OddsUpfrontModule,
        Modules.NonMinimalBetPopup
    ],
    views: [
        // initial
        Modules.SystemPopupsView,
        Modules.LoadingScreenViewOld,
        Modules.OddsUpfrontReactView,
        Modules.WaitingView,
        // free rounds
        Modules.PromotionCounterView,

        Modules.BigWinView,
        Modules.WinPresentationView,
        Modules.BackgroundView,
        Modules.GenericPopupView,
        Modules.GameFrameView,
        /* develblock:start */
        Modules.CheatsView,
        /* develblock:end */
        Modules.KeypadView,
        Modules.AutoplayViewDesktop,
        Modules.SpinButtonView,
        Modules.KeypadInfoElementsView,
        Modules.KeypadControlsView,
        Modules.WinMeterView,
        Modules.BottomPanelFullScreenView,
        Modules.WaterMarkViewDesktop,

        Modules.BuyPopUpView,
        Modules.CoinFlightView,
        Modules.BuyBonusButtonView,
        Modules.BuyBonusBalanceView,

        Modules.BuyFeatureView,
        // mobiles views
        Modules.KeypadViewMobileLandscape,
        Modules.KeypadViewMobilePortrait,
        Modules.KeypadViewMobile,

        Modules.SpinButtonViewMobileLandscape,
        Modules.SpinButtonViewMobilePortrait,
        Modules.SpinButtonViewMobile,

        Modules.KeypadInfoElementsViewMobilePortrait,
        Modules.KeypadInfoElementsViewMobileLandscape,
        Modules.KeypadInfoElementsViewMobile,

        Modules.KeypadControlsViewMobileLandscape,
        Modules.KeypadControlsViewMobilePortrait,
        Modules.KeypadControlsViewMobile,

        Modules.AutoplayViewMobileLandscape,
        Modules.AutoplayViewMobilePortrait,
        Modules.AutoplayViewMobile,

        Modules.MenuBarViewMobileLandscape,
        Modules.MenuBarViewMobilePortrait,
        Modules.MenuBarViewMobile,

        Modules.WinMeterViewMobileLandscape,
        Modules.WinMeterViewMobilePortrait,
        Modules.WinMeterViewMobile,

        Modules.WaterMarkViewMobilePortrait,
        Modules.WaterMarkViewMobileLandscape,
        Modules.WaterMarkViewMobile,

        Modules.BuyBonusButtonViewLandscape,
        Modules.BuyBonusButtonViewPortrait,
        Modules.BuyBonusButtonViewMobile,

        Modules.BuyBonusBalanceViewLandscape,
        Modules.BuyBonusBalanceViewPortrait,
        Modules.BuyBonusBalanceViewMobile
    ]
};

export function getCommonFixedScreenSizeModules(): {
    modules: ComponentModuleConfig[];
    views: ComponentModuleViewConfig[];
    } {
    return {
        modules: [Modules.GameFrameModule, Modules.WaterMarkModule],
        views: [Modules.BottomPanelView]
    };
}
